import { IColumnsBlock, IContentBlock, IDesignBlock } from "../types/iblock";
import {
  IColumnsBlockData,
  IButtonBlockData,
  IDividerBlockData,
  IHeadingBlockData,
  ITextBlockData,
  IImageBlockData,
  IHeroSliderBlockData,
  IVideoBlockData,
  IHtmlBlockData,
  ISocialsBlockData,
} from "../types/content-block-data";
import { IBodyStyles, ITypoStyles, ICssContent } from "./state-interfaces";

// Import all block serializers
import {
  fromHeadingBlockToHTML,
  fromHeadingBlockToJSON,
  fromJSONToHeadingBlock,
} from "../utils/serialization/heading";
import {
  fromTextBlockToHTML,
  fromTextBlockToJSON,
  fromJSONToTextBlock,
} from "../utils/serialization/text";
import {
  fromColumnsBlockToHTML,
  fromColumnsBlockToJSON,
  fromJSONToColumnsBlock,
} from "../utils/serialization/columns";
import {
  fromButtonBlockToHTML,
  fromButtonBlockToJSON,
  fromJSONToButtonBlock,
} from "../utils/serialization/button";
import {
  fromDividerBlockToHTML,
  fromDividerBlockToJSON,
  fromJSONToDividerBlock,
} from "../utils/serialization/divider";
import {
  fromImageBlockToHTML,
  fromImageBlockToJSON,
  fromJSONToImageBlock,
} from "../utils/serialization/image";
import {
  fromHeroSliderBlockToHTML,
  fromHeroSliderBlockToJSON,
  fromJSONToHeroSliderBlock,
} from "../utils/serialization/hero-slider";
import {
  fromVideoBlockToHTML,
  fromVideoBlockToJSON,
  fromJSONToVideoBlock,
} from "../utils/serialization/video";
import {
  fromSocialsBlockToHTML,
  fromSocialsBlockToJSON,
  fromJSONToSocialsBlock,
} from "../utils/serialization/socials";
import {
  fromHtmlBlockToHTML,
  fromHtmlBlockToJSON,
  fromJSONToHtmlBlock,
} from "../utils/serialization/html";
import pageBuilderStore from "./pageBuilderStore";
import blocksManager from "./blocks-manager";
import contentManager from "./content-manager";
import bodyStylesManager from "./body-styles-manager";
import { generateUniqueKey } from "../lib/page-builder-lib";

/**
 * @class SerializationManager
 * @description
 * Centralized utility class for serializing and deserializing all block data structures
 * used by the PageBuilder application.
 *
 * This manager is responsible for converting between in-memory runtime representations
 * (`IDesignBlock[]`, `IContentBlock`, etc.) and their corresponding plain JSON forms
 * suitable for storage, export, and rehydration.
 *
 * ## Features:
 * - Serializes complex, nested block structures (including columns/children) to clean, data-only JSON.
 * - Deserializes JSON back into fully hydrated in-memory blocks, generating all required runtime/UI metadata.
 * - Supports recursive traversal for container/structural blocks.
 * - Delegates block-specific serialization to per-block serializer modules (heading, text, columns, etc).
 * - Maintains separation of concerns:
 *    - All UI/runtime fields (`designBlockId`, refs, etc.) are excluded from serialization.
 *    - Only data fields (`IContentBlock`, `blockData`, etc.) are serialized.
 * - Provides a single authoritative interface for (de)serializing the entire page state.
 *
 * ## Typical Usage:
 * ```ts
 * const json = SerializationManager.toJSON(designBlocks); // IDesignBlock[] → JSON
 * const blocks = SerializationManager.fromJSON(json);     // JSON → IDesignBlock[]
 * ```
 *
 * ## Method Categories:
 * - **Serialization (to JSON)**:
 *    - `toJSON(blocks: IDesignBlock[]): object[]`
 *    - Per-block: `fromHeadingBlockToJSON`, `fromColumnsBlockToJSON`, etc.
 * - **Deserialization (from JSON)**:
 *    - `fromJSON(json: any[]): IDesignBlock[]`
 *    - Per-block: `fromJSONToHeadingBlock`, `fromJSONToColumnsBlock`, etc.
 * - **Helpers**: Internal recursive methods for traversing children, handling block type switching, etc.
 *
 * ## Design Philosophy:
 * - This class serves as the architectural bridge between UI state and persisted/exported data.
 * - Ensures robust, type-safe migrations and supports future extensibility (e.g., undo/redo snapshots, templates).
 *
 * @author Chen Weizhi
 * @since 2025-06-21
 * @file serialization-manager.ts
 */
class SerializationManager {
  /**
   * Converts a single IContentBlock to HTML.
   */
  fromContentBlockToHTML(block: IDesignBlock): string {
    switch (block.contentBlock.blockType) {
      case "heading":
        return fromHeadingBlockToHTML(block.contentBlock);
      case "text":
        return fromTextBlockToHTML(block.contentBlock);
      case "columns":
        return fromColumnsBlockToHTML(block, this.fromContentBlockToHTML);
      case "button":
        return fromButtonBlockToHTML(block.contentBlock);
      case "divider":
        return fromDividerBlockToHTML(block.contentBlock);
      case "image":
        return fromImageBlockToHTML(block.contentBlock);
      case "hero_slider":
        return fromHeroSliderBlockToHTML(block.contentBlock);
      case "video":
        return fromVideoBlockToHTML(block.contentBlock);
      case "socials":
        return fromSocialsBlockToHTML(block.contentBlock);
      case "html":
        return fromHtmlBlockToHTML(block.contentBlock);
      default:
        return "";
    }
  }

  /**
   * Converts an array of IContentBlock to a single HTML string (page export/preview).
   */
  fromBlocksToHTML(blocks: IDesignBlock[], pretty = false): string {

    const isEmail = pageBuilderStore.getIsEmail();

    // 1. Find all columns blocks for suffixing
    const allColumnsBlocks = blocks.filter(
      (b) => b.contentBlock.blockType === "columns"
    );
    const isMultiple = allColumnsBlocks.length > 1;
    let columnsIndex = 0;

    let html = blocks
      .map((block) => {
        let html = "";
        if (block.contentBlock.blockType === "columns") {
          const suffix = isMultiple
            ? `-${String(++columnsIndex).padStart(2, "0")}`
            : "";
          html = fromColumnsBlockToHTML(
            block,
            this.fromContentBlockToHTML.bind(this)
          );
        } else {
          html = this.fromContentBlockToHTML(block);
        }

        if (!html) return "";

        if (!pretty) return html;

        // Only indent multiline blocks
        const isMultiline = html.includes("\n");
        return isMultiline
          ? html
              .split("\r\n")
              .map((line) => "    " + line)
              .join("\r\n")
          : "    " + html;
      })
      .filter(Boolean)
      .join(pretty ? "\r\n" : "");
    
      // Check if the user is in email template editing mode.
    if (isEmail) {
      // Optionally, add any extra table attributes for email compatibility (border, width cellpadding, cellspacing, etc).
      html = `<table width="100%" border="0" cellspacing="0" cellpadding="0" style="border-collapse:collapse;">\n${html}\n</table>`
    }

    return html;
  }

  /**
   * Converts a single IContentBlock to a JSON-serializable object.
   */
  fromContentBlockToJSON(block: IContentBlock): object {
    switch (block.blockType) {
      case "heading":
        return fromHeadingBlockToJSON(block);
      case "text":
        return fromTextBlockToJSON(block);
      case "columns":
        return fromColumnsBlockToJSON(block, this.fromContentBlockToJSON.bind(this));
      case "button":
        return fromButtonBlockToJSON(block);
      case "divider":
        return fromDividerBlockToJSON(block);
      case "image":
        return fromImageBlockToJSON(block);
      case "hero_slider":
        return fromHeroSliderBlockToJSON(block);
      case "video":
        return fromVideoBlockToJSON(block);
      case "socials":
        return fromSocialsBlockToJSON(block);
      case "html":
        return fromHtmlBlockToJSON(block);
      default:
        return {};
    }
  }

  /**
   * Converts an array of IContentBlock to a JSON array (for saving/exporting a page).
   */
  fromBlocksToJSON(blocks: IDesignBlock[]): object[] {
    return blocks.map((designBlock) =>
      this.fromContentBlockToJSON(designBlock.contentBlock)
    );
  }

  /**
   * Converts a single JSON (`IDesignBlock.contentBlock`) object to an IDesignBlock.
   * @remarks
   * - Since we only serialize the `IDesignBlock.contentBlock` (`IContentBlock`), the IDesignBlock relevant data
   *   will only be placeholder data.
   * - The real data that we are parsing from `json` is the `contentBlock` values.
   */
  fromJSONToContentBlock(json: any, context: "canvas" | "columns" = "canvas"): IDesignBlock {
    console.log("json:", json);
    switch (json.blockType) {
      case "heading":
        return fromJSONToHeadingBlock(json, context);
      case "text":
        return fromJSONToTextBlock(json, context);
      case "columns":
        console.log(json);
        return fromJSONToColumnsBlock(json);
      case "button":
        return fromJSONToButtonBlock(json, context);
      case "divider":
        return fromJSONToDividerBlock(json, context);
      case "image":
        return fromJSONToImageBlock(json, context);
      case "hero_slider":
        return fromJSONToHeroSliderBlock(json, context);
      case "video":
        return fromJSONToVideoBlock(json, context);
      case "socials":
        return fromJSONToSocialsBlock(json, context);
      case "html":
        return fromJSONToHtmlBlock(json, context);
      default:
        return {
          designBlockId: crypto.randomUUID(), // always generate fresh
          designBlockRef: undefined,
          contentBlock: json,
        };
    }
  }

  /**
   * Converts a JSON array to IContentBlock[].
   */
  fromJSONToBlocks(jsonArray: any): IDesignBlock[] {
    return jsonArray.map(this.fromJSONToContentBlock);
  }

  /**
   * Normalizes a raw CSS selector from imported styles into a key for ITypoStyles.
   *
   * - Maps known class selectors (e.g., ".title", ".caption", ".quote") to core role keys.
   * - Maps the "a" tag to the "links" role, and "p" to the "p" role.
   * - Removes the leading dot from custom class selectors (e.g., ".my-block" → "my-block").
   * - Leaves HTML tags (e.g., "h1") and other selectors as-is.
   *
   * This utility ensures imported CSS selectors always map to the correct core or custom role
   * in ITypoStyles, preventing duplicate or missed mappings during import and export.
   *
   * @param selector   The raw CSS selector (e.g., ".title", "h1", "a", ".carousel-caption").
   * @returns          The normalized ITypoStyles key (e.g., "title", "links", "carousel-caption", "h1").
   *
   * @example
   *   normalizeSelectorForTypoStyles('.title'); // "title"
   *   normalizeSelectorForTypoStyles('a');      // "links"
   *   normalizeSelectorForTypoStyles('.hero');  // "hero"
   *   normalizeSelectorForTypoStyles('h2');     // "h2"
   */
  normalizeSelectorForTypoStyles(selector: string): string {
    if (selector === ".title") return "title";
    if (selector === ".caption") return "caption";
    if (selector === ".quote") return "quote";
    if (selector === "a") return "links";
    if (selector === "p") return "p";
    if (selector.startsWith(".")) return selector.substring(1);
    return selector;
  }

  /**
   * Converts an array of ICssContent objects (typically parsed from a CSS JSON file or API)
   * into a fully-typed IBodyStyles state object, ready for use in the PageBuilder.
   *
   * - Deep clones the provided defaults to avoid mutation.
   * - Normalizes selector names to match core and custom ITypoStyles keys.
   * - Merges all imported style properties into the corresponding role (core or custom) in ITypoStyles.
   * - Ensures all core typography keys are present and initialized (with defaults if missing).
   * - Skips any empty or undefined style entries.
   *
   * @param cssContent   Array of { selector, styles } objects from parsed CSS JSON or similar source.
   * @param defaults     Initial/default IBodyStyles object, used as the merge base.
   * @returns            Fully populated IBodyStyles state, safe for UI rendering and serialization.
   *
   * @example
   *   const newState = serializationManager.fromCSSContentToBodyStyles(cssContent, initialBodyStyles);
   */
  fromCSSContentToBodyStyles(
    cssContent: ICssContent[],
    defaults: IBodyStyles
  ): IBodyStyles {
    // Start with a deep clone of your core/defaults to avoid mutation
    const state: IBodyStyles = JSON.parse(JSON.stringify(defaults));

    // Build a list of core keys for comparison
    const coreKeys = Object.keys(pageBuilderStore.CORE_TYPOGRAPHY_ROLES);

    // Ensure .typography is always initialized
    if (!state.typography) {
      state.typography = {
        ...pageBuilderStore.CORE_TYPOGRAPHY_ROLES,
      } as ITypoStyles;
    }
    const typography = state.typography as ITypoStyles; // Safe after above

    for (const { selector, styles } of cssContent) {
      const normalizedKey = this.normalizeSelectorForTypoStyles(selector);
      const mappedRole = bodyStylesManager.mapCssToTypographyRole(styles);
      if (!mappedRole) continue;

      typography[normalizedKey] = {
        ...typography[normalizedKey],
        ...mappedRole,
      };
    }

    // Ensure all core keys exist
    for (const key of coreKeys) {
      if (typeof typography[key] === "undefined" || typography[key] == null) {
        typography[key] = { ...pageBuilderStore.CORE_TYPOGRAPHY_ROLES[key] };
      }
    }

    return state;
  }

  /**
   * Serializes the entire IBodyStyles state object into a complete CSS string for export or preview.
   *
   * - Combines layout (container/background/max-width/etc.) and optional body typography into a single `body { ... }` block.
   * - Serializes all other typography roles and custom selectors via typographyToCss, skipping "body" to avoid duplication.
   * - Ensures the returned CSS string is ready for direct output, download, or API publishing.
   *
   * @param bodyStyles   The full IBodyStyles state object to be serialized.
   * @returns            The complete CSS string representing all styles in the current state.
   *
   * @example
   *   const cssString = serializationManager.fromBodyStylesToCssString(state.bodyStyles);
   *   // cssString now contains all style blocks as valid CSS text
   */
  fromBodyStylesToCssString(bodyStyles: IBodyStyles): string {
    let css = "";

    // Output the combined layout/typography "body" block
    css += bodyStylesManager.bodyToCss(
      bodyStyles.layout,
      bodyStyles.typography?.body
    );

    // Output all other typography blocks, skipping "body"
    if (bodyStyles.typography != null)
      css += bodyStylesManager.typographyToCss(bodyStyles.typography, ["body"]);

    return css;
  }

  /**
   * Serializes the IBodyStyles state object into an array of ICssContent blocks,
   * suitable for export as CSS JSON, API transmission, or re-import.
   *
   * - Adds a "body" block containing all relevant layout properties (background, maxWidth, etc).
   * - Serializes all typography roles—both core and custom—using appropriate CSS selectors.
   * - For each role, outputs only defined properties (skips empty blocks).
   * - Adds a ":hover" block for any role or custom selector that defines a hover color.
   * - Returns a flat array of { selector, styles } objects for further processing or storage.
   *
   * @param state     The full IBodyStyles state object to serialize.
   * @returns         Array of ICssContent objects ({ selector, styles }) representing all style blocks.
   *
   * @example
   *   const cssContent = serializationManager.fromStylesToCssContent(state.bodyStyles);
   *   // cssContent can now be saved as JSON, used for preview, or re-imported.
   */
  fromStylesToCssContent(state: IBodyStyles): ICssContent[] {
    const cssArr: ICssContent[] = [];

    // 1. Layout — main body selector
    const layout = state.layout;
    const bodyStyles: Record<string, string> = {};
    if (layout?.backgroundColor)
      bodyStyles["background-color"] = layout.backgroundColor;
    if (layout?.padding)
      bodyStyles["padding"] =
        blocksManager.dimensionToCss(layout.padding) || "";
    if (layout?.margin)
      bodyStyles["margin"] =
        blocksManager.dimensionToCss(layout.margin) || "";
    if (layout?.maxWidth)
      bodyStyles["max-width"] =
        blocksManager.dimensionToCss(layout.maxWidth) || "";
    // ...add more layout props as needed

    cssArr.push({
      selector: "body",
      styles: bodyStyles,
      css: `body { ${Object.entries(bodyStyles)
        .map(([k, v]) => `${k}: ${v};`)
        .join(" ")} }`,
    });

    // 2. Typography roles (core and custom)
    const typo: ITypoStyles = state.typography!;
    const roleMap: Record<string, string> = {
      p: "p",
      caption: ".caption",
      quote: ".quote",
      links: "a",
      // h1-h6 are exported as themselves, so not in the map
    };

    const CORE_KEYS: (keyof ITypoStyles)[] = [
      "title",
      "h1",
      "h2",
      "h3",
      "h4",
      "h5",
      "h6",
      "p",
      "caption",
      "quote",
      "links",
    ];
    const processed = new Set<string>();

    // Handle core roles first
    for (const key of Object.keys(typo)) {
      const role = typo[key];
      if (!role) continue;
      processed.add(key.toString());

      const selector = roleMap[key] ? roleMap[key] : key; // Map as needed or use key directly
      const styles: Record<string, string> = {};

      if (role.fontFamily) styles["font-family"] = role.fontFamily;
      if (role.fontSize)
        styles["font-size"] =
          blocksManager.dimensionToCss(role.fontSize) || "";
      if (role.fontWeight) styles["font-weight"] = role.fontWeight;
      if (role.lineHeight)
        styles["line-height"] =
          blocksManager.dimensionToCss(role.lineHeight) || "";
      if (role.letterSpacing)
        styles["letter-spacing"] =
          blocksManager.dimensionToCss(role.letterSpacing) || "";
      if (role.color) styles["color"] = role.color;
      if (role.fontStyle) styles["font-style"] = role.fontStyle;
      if (role.textDecoration) styles["text-decoration"] = role.textDecoration;
      if (role.textTransform) styles["text-transform"] = role.textTransform;

      // Only add styles if not empty
      if (Object.keys(styles).length > 0) {
        cssArr.push({
          selector,
          styles,
          css: `${selector} { ${Object.entries(styles)
            .map(([k, v]) => `${k}: ${v};`)
            .join(" ")} }`,
        });
      }

      // Hover color for links (generalized to any role with hoverColor)
      if (role.hoverColor) {
        cssArr.push({
          selector: `${selector}:hover`,
          styles: { color: role.hoverColor },
          css: `${selector}:hover { color: ${role.hoverColor}; }`,
        });
      }
    }

    // 3. Handle any additional custom selectors/roles
    for (const key of Object.keys(typo)) {
      if (processed.has(key)) continue; // Already handled core
      const role = typo[key];
      if (!role) continue;
      const selector = key; // For custom, use key as-is
      const styles: Record<string, string> = {};

      if (role.fontFamily) styles["font-family"] = role.fontFamily;
      if (role.fontSize)
        styles["font-size"] =
          blocksManager.dimensionToCss(role.fontSize) || "";
      if (role.fontWeight) styles["font-weight"] = role.fontWeight;
      if (role.lineHeight)
        styles["line-height"] =
          blocksManager.dimensionToCss(role.lineHeight) || "";
      if (role.letterSpacing)
        styles["letter-spacing"] =
          blocksManager.dimensionToCss(role.letterSpacing) || "";
      if (role.color) styles["color"] = role.color;
      if (role.fontStyle) styles["font-style"] = role.fontStyle;
      if (role.textDecoration) styles["text-decoration"] = role.textDecoration;
      if (role.textTransform) styles["text-transform"] = role.textTransform;

      if (Object.keys(styles).length > 0) {
        cssArr.push({
          selector,
          styles,
          css: `${selector} { ${Object.entries(styles)
            .map(([k, v]) => `${k}: ${v};`)
            .join(" ")} }`,
        });
      }

      // Generalized hover support for custom selectors
      if (role.hoverColor) {
        cssArr.push({
          selector: `${selector}:hover`,
          styles: { color: role.hoverColor },
          css: `${selector}:hover { color: ${role.hoverColor}; }`,
        });
      }
    }

    return cssArr;
  }

  // We will begin our functions here for serializing the different Content Block's CSS into raw CSS
  // or cssContent: ICssContent[]

  /**
   * Serializes a Columns block's layout and styling data into an array of CSS content objects.
   * Generates all necessary CSS for the specified number of columns, applying the provided selector as a prefix if specified.
   *
   * @param {IColumnsBlockData} blockData - The data object containing configuration and styles for the Columns block.
   * @param {number} colCount - The number of columns to generate CSS for.
   * @param {string} [selector=""] - Optional CSS selector prefix to scope the generated styles (default is empty).
   * @returns {ICssContent[]} An array of CSS content objects representing the styles for the Columns block and its cells.
   *
   * @example
   * const cssArray = fromColumnsBlockToCssContent(blockData, 3, ".my-columns-block");
   */
  fromColumnsBlockToCssContent(
    blockData: IColumnsBlockData,
    colCount: number,
    selector: string = ""
  ): ICssContent[] {
    const cssArr: ICssContent[] = [];

    // Main columns block style
    cssArr.push({
      selector,
      styles: {
        display: "grid",
        "grid-template-columns": `repeat(${colCount}, 1fr)`,
        gap: blockData.columnGap?.value
          ? `${blockData.columnGap.value}${blockData.columnGap.unit || "px"}`
          : "8px",
        "row-gap": blockData.rowGap?.value
          ? `${blockData.rowGap.value}${blockData.rowGap.unit || "px"}`
          : "8px",
      },
      css: `.${selector} { 
          display: grid;
          grid-template-columns: repeat(${colCount}, 1fr);
          gap: ${blockData.columnGap?.value ? `${blockData.columnGap.value}${blockData.columnGap.unit || "px"}` : "8px"};
          row-gap: ${blockData.rowGap?.value ? `${blockData.rowGap.value}${blockData.rowGap.unit || "px"}` : "8px"};
        }`,
    });

    // Columns cell style (for all cells)
    cssArr.push({
      selector: `${selector}-cell`,
      styles: {
        padding: blocksManager.dimensionToCss(blockData.cellPadding),
        margin: blocksManager.dimensionToCss(blockData.cellMargin),
        "border-radius": blocksManager.dimensionToCss(blockData.cellRadius),
        overflow: parseInt(blockData.cellRadius?.topLeft!) > 0 
          || parseInt(blockData.cellRadius?.topRight!) > 0
          || parseInt(blockData.cellRadius?.bottomLeft!) > 0
          || parseInt(blockData.cellRadius?.bottomRight!) > 0  ? "hidden" : "auto"
      },
      css: `.${selector}-cell { 
          padding: ${blocksManager.dimensionToCss(blockData.cellPadding)};
          margin: ${blocksManager.dimensionToCss(blockData.cellMargin)};
          border-radius: ${blocksManager.dimensionToCss(blockData.cellRadius)};
          overflow: ${parseInt(blockData.cellRadius?.topLeft!) > 0 
            || parseInt(blockData.cellRadius?.topRight!) > 0
            || parseInt(blockData.cellRadius?.bottomLeft!) > 0
            || parseInt(blockData.cellRadius?.bottomRight!) > 0  ? "hidden" : "auto"};
        }`,
    });

    // (You can add more entries here as needed)

    return cssArr;
  }

  /**
   * Serializes a Button block's styling data into an array of CSS content objects.
   * Produces all necessary CSS styles for the button using the provided selector as a prefix if specified.
   *
   * @param {IButtonBlockData} blockData - The data object containing configuration and styles for the Button block.
   * @param {string} [selector=""] - Optional CSS selector prefix to scope the generated styles (default is empty).
   * @returns {ICssContent[]} An array of CSS content objects representing the styles for the Button block.
   *
   * @example
   * const cssArray = fromButtonBlockToCssContent(buttonData, ".custom-btn");
   */
  fromButtonBlockToCssContent(
    blockData: IButtonBlockData,
    selector: string = ""
  ): ICssContent [] {
    const cssArr: ICssContent[] = [];

    cssArr.push({
      selector: selector,
      styles: {
        "color": blockData.color || "#fff",
        "background-color": blockData.backgroundColor || "#2563eb",
        "font-family": blockData.fontFamily || "Inter, sans-serif",
        "font-size": blockData.fontSize?.value
          ? `${blockData.fontSize.value}${blockData.fontSize.unit || "rem"}`
          : "1rem",
        "font-weight": blockData.fontWeight || "500",
        "line-height": blockData.lineHeight?.value
          ? `${blockData.lineHeight.value}${blockData.lineHeight.unit || ""}`
          : "1.5rem",
        "letter-spacing": blockData.letterSpacing?.value
          ? `${blockData.letterSpacing.value}${blockData.letterSpacing.unit || "px"}`
          : "0px",
        "text-decoration": blockData.textDecoration || "none",
        "padding": blocksManager.dimensionToCss(blockData.padding) || "8px",
        "margin": blocksManager.dimensionToCss(blockData.margin) || "8px",
        "border-radius":
          blocksManager.dimensionToCss(blockData.borderRadius) || "8px",
        "border-style": blockData.borderStyle || "none",
        "border-width": blockData.borderWidth?.value
          ? `${blockData.borderWidth.value}${blockData.borderWidth.unit || "px"}`
          : "0px",
        "border-color": blockData.borderColor || "transparent",
        "width":
          blockData.width?.value && blockData.width.value !== "auto"
            ? `${blockData.width.value}${blockData.width.unit || "px"}`
            : "100%",
        // Add any other properties you want to serialize
      },
      css: `.${selector} {
        color: ${blockData.color || "#fff"};
        background-color: ${blockData.backgroundColor || "#2563eb"};
        font-family: ${blockData.fontFamily || "Inter, sans-serif"};
        font-size: ${blockData.fontSize?.value ? `${blockData.fontSize.value}${blockData.fontSize.unit || "rem"}` : "1rem"};
        font-weight: ${blockData.fontWeight || "500"};
        line-height: ${blockData.lineHeight?.value ? `${blockData.lineHeight.value}${blockData.lineHeight.unit || ""}` : "normal"};
        letter-spacing: ${blockData.letterSpacing?.value ? `${blockData.letterSpacing.value}${blockData.letterSpacing.unit || "px"}` : "normal"};
        text-decoration: ${blockData.textDecoration || "none"};
        padding: ${blocksManager.dimensionToCss(blockData.padding) || "8px"};
        margin: ${blocksManager.dimensionToCss(blockData.margin) || "8px"};
        border-radius: ${blocksManager.dimensionToCss(blockData.borderRadius) || "8px"};
        border-style: ${blockData.borderStyle || "none"};
        border-width: ${blockData.borderWidth?.value ? `${blockData.borderWidth.value}${blockData.borderWidth.unit || "px"}` : "0px"};
        border-color: ${blockData.borderColor || "transparent"};
        width: ${
          blockData.width?.value && blockData.width.value !== "auto"
            ? `${blockData.width.value}${blockData.width.unit || "px"}`
            : "auto"
        };
        transition: all 0.15s;
      }
      
      `.trim(),
    });

    cssArr.push({
      selector: `${selector}:hover`,
      styles: {
        "color": blockData.hoverColor || blockData.color || "#fff",
        "background-color": blockData.hoverBackgroundColor || "#1d4ed8"
      },
      css: `
      .${selector}:hover {
        color: ${blockData.hoverColor || blockData.color || "#fff"};
        background-color: ${blockData.hoverBackgroundColor || "#2563eb"};
      }`.trim(),
    });

    return cssArr;
  }

  /**
   * Serializes a Divider block's styling data into a CSS content object.
   * Generates all necessary CSS for the divider, applying the provided selector as a prefix if specified.
   *
   * @param {IDividerBlockData} blockData - The data object containing configuration and styles for the Divider block.
   * @param {string} [selector=""] - Optional CSS selector prefix to scope the generated styles (default is empty).
   * @returns {ICssContent} A CSS content object representing the styles for the Divider block.
   *
   * @example
   * const cssObj = fromDividerBlockToCssContent(dividerData, ".my-divider");
   */
  fromDividerBlockToCssContent(
    blockData: IDividerBlockData,
    selector: string = ""
  ): ICssContent {
    return {
      selector: selector,
      styles: {
        "width": blockData.width?.value
          ? `${blockData.width.value}${blockData.width.unit || "%"}`
          : "100%",
        "height": blockData.height?.value
          ? `${blockData.height.value}${blockData.height.unit || "px"}`
          : "1px",
        "background-color": blockData.color || "#CCCCCC",
        "padding": `${blocksManager.dimensionToCss(blockData.padding) || "0px"}`,
        "margin": `${blocksManager.dimensionToCss(blockData.margins) || "0px"}`,
        "border": "none",
        "display": "block",
      },
      css: `.${selector} {
        width: ${
          blockData.width?.value
            ? `${blockData.width.value}${blockData.width.unit || "%"}`
            : "100%"
        };
        height: ${
          blockData.height?.value
            ? `${blockData.height.value}${blockData.height.unit || "px"}`
            : "1px"
        };
        background-color: ${blockData.color || "#CCCCCC"};
        padding: ${blocksManager.dimensionToCss(blockData.padding) || "0px"};
        margin: ${blocksManager.dimensionToCss(blockData.margins) || "0px"};
        border: none;
        display: block;
      }`.trim(),
    };
  }

  /**
   * Serializes a Heading block's styling data into a CSS content object.
   * Generates the CSS necessary for the heading element, using the provided selector as a prefix if specified.
   *
   * @param {IHeadingBlockData} blockData - The data object containing configuration and styles for the Heading block.
   * @param {string} [selector=""] - Optional CSS selector prefix to scope the generated styles (default is empty).
   * @returns {ICssContent} A CSS content object representing the styles for the Heading block.
   *
   * @example
   * const cssObj = fromHeadingBlockToCssContent(headingData, ".my-heading");
   */
  fromHeadingBlockToCssContent(
    blockData: IHeadingBlockData,
    selector: string = ""
  ): ICssContent {
    return {
      selector: selector,
      styles: {
        "color": blockData.color || "#ffffff",
        "font-family": blockData.fontFamily || "Inter, sans-serif",
        "font-size": blockData.fontSize?.value
          ? `${blockData.fontSize.value}${blockData.fontSize.unit || "rem"}`
          : "1rem",
        "font-weight": blockData.fontWeight || "700",
        "line-height": blockData.lineHeight?.value
          ? `${blockData.lineHeight.value}${blockData.lineHeight.unit || ""}`
          : "1.5rem",
        "letter-spacing": blockData.letterSpacing?.value
          ? `${blockData.letterSpacing.value}${blockData.letterSpacing.unit || "px"}`
          : "0px",
        "text-decoration": blockData.textDecoration || "none",
        "text-transform": blockData.textTransform || "none",
        "text-align": blockData.alignment || "left",
        // Add any other properties you want to serialize
      },
      css: `.${selector} {
        color: ${blockData.color || "#ffffff"};
        font-family: ${blockData.fontFamily || "Inter, sans-serif"};
        font-size: ${blockData.fontSize?.value ? `${blockData.fontSize.value}${blockData.fontSize.unit || "rem"}` : "1rem"};
        font-weight: ${blockData.fontWeight || "700"};
        line-height: ${blockData.lineHeight?.value ? `${blockData.lineHeight.value}${blockData.lineHeight.unit || ""}` : "normal"};
        letter-spacing: ${blockData.letterSpacing?.value ? `${blockData.letterSpacing.value}${blockData.letterSpacing.unit || "px"}` : "normal"};
        text-decoration: ${blockData.textDecoration || "none"};
        text-transform: ${blockData.textTransform || "none"};
        text-align: ${blockData.alignment || "left"};
        transition: all 0.15s;
      }`.trim(),
    };
  }

  /**
   * Serializes a Text block's styling data into a CSS content object.
   * Generates the CSS necessary for the text area, using the provided selector as a prefix if specified.
   *
   * @param {ITextBlockData} blockData - The data object containing configuration and styles for the Text block.
   * @param {string} [selector=""] - Optional CSS selector prefix to scope the generated styles (default is empty).
   * @returns {ICssContent} A CSS content object representing the styles for the Text block.
   *
   * @example
   * const cssObj = fromTextBlockToCssContent(textData, ".custom-text");
   */
  fromTextBlockToCssContent(
    blockData: ITextBlockData,
    selector: string = ""
  ): ICssContent {
    return {
      selector: selector,
      styles: {
        "color": blockData.color || "#ffffff",
        "font-family": blockData.fontFamily || "Inter, sans-serif",
        "font-size": blockData.fontSize?.value
          ? `${blockData.fontSize.value}${blockData.fontSize.unit || "rem"}`
          : "1rem",
        "font-weight": blockData.fontWeight || "700",
        "line-height": blockData.lineHeight?.value
          ? `${blockData.lineHeight.value}${blockData.lineHeight.unit || ""}`
          : "1.5rem",
        "letter-spacing": blockData.letterSpacing?.value
          ? `${blockData.letterSpacing.value}${blockData.letterSpacing.unit || "px"}`
          : "0px",
        "text-decoration": blockData.textDecoration || "none",
        "text-transform": blockData.textTransform || "none",
        // Add any other properties you want to serialize
      },
      css: `
      .${selector} {
        color: ${blockData.color || "#ffffff"};
        font-family: ${blockData.fontFamily || "Inter, sans-serif"};
        font-size: ${blockData.fontSize?.value ? `${blockData.fontSize.value}${blockData.fontSize.unit || "rem"}` : "1rem"};
        font-weight: ${blockData.fontWeight || "700"};
        line-height: ${blockData.lineHeight?.value ? `${blockData.lineHeight.value}${blockData.lineHeight.unit || ""}` : "normal"};
        letter-spacing: ${blockData.letterSpacing?.value ? `${blockData.letterSpacing.value}${blockData.letterSpacing.unit || "px"}` : "normal"};
        text-decoration: ${blockData.textDecoration || "none"};
        text-transform: ${blockData.textTransform || "none"};
        transition: all 0.15s;
      }`.trim(),
    };
  }

  /**
   * Serializes an Image block's styling data into a CSS content object.
   * Generates the CSS necessary for the image element, using the provided selector as a prefix if specified.
   *
   * @param {IImageBlockData} blockData - The data object containing configuration and styles for the Image block.
   * @param {string} [selector=""] - Optional CSS selector prefix to scope the generated styles (default is empty).
   * @returns {ICssContent} A CSS content object representing the styles for the Image block.
   *
   * @example
   * const cssObj = fromImageBlockToCssContent(imageData, ".my-image-block");
   */
  fromImageBlockToCssContent(
    blockData: IImageBlockData,
    selector: string = ""
  ) {
    return {
      selector: selector,
      styles: {
        width: blockData.width?.value
          ? `${blockData.width.value}${blockData.width.unit || "%"}`
          : "100%",
        height: blockData.height?.value && blockData.height?.value !== ""
          ? `${blockData.height.value}${blockData.height.unit || "px"}`
          : "auto",
        "object-fit": blockData.imageFit || "cover",
        "object-position": blockData.imagePosition || "center",
        margin: `${blocksManager.dimensionToCss(blockData.margin) || "0px"}`,
        "border-radius":
          blocksManager.dimensionToCss(blockData.borderRadius) || "8px",
        display: "block",
        ...(blockData.alignment === "center"
          ? { marginLeft: "auto", marginRight: "auto" }
          : blockData.alignment === "right"
            ? { marginLeft: "auto", marginRight: "0" }
            : { marginLeft: "0", marginRight: "auto" }),
        // Add any other properties you want to serialize
      },
      css: `
      .${selector} {
        width: ${
          blockData.width?.value
            ? `${blockData.width.value}${blockData.width.unit || "%"}`
            : "100%"
        };
        height: ${
          blockData.height?.value && blockData.height?.value !== ""
            ? `${blockData.height.value}${blockData.height.unit || "px"}`
            : "auto"
        };
        margin: ${blocksManager.dimensionToCss(blockData.margin) || "0px"};
        display: "block";
        ${
          blockData.alignment === "center"
            ? "margin-left: auto; margin-right: auto;"
            : blockData.alignment === "right"
              ? "margin-left: auto; margin-right: 0;"
              : "margin-left: 0; margin-right: auto;"
        }
        object-fit: ${blockData.imageFit || "cover"};
        object-position: ${blockData.imagePosition || "center"};
        border-radius: ${blocksManager.dimensionToCss(blockData.borderRadius) || "8px"};
        transition: all 0.15s;
      }`.trim(),
    };
  }

  /**
   * Serializes a Hero Slider block's styling data into an array of CSS content objects.
   * Generates all necessary CSS for the Hero Slider, including per-slide and global styles,
   * using the provided selector as a prefix if specified.
   *
   * @param {IHeroSliderBlockData} blockData - The data object containing configuration and styles for the Hero Slider block.
   * @param {string} [selector=""] - Optional CSS selector prefix to scope the generated styles (default is empty).
   * @returns {ICssContent[]} An array of CSS content objects representing the styles for the Hero Slider block and its slides.
   *
   * @example
   * const cssArray = fromHeroSliderBlockToCssContent(heroSliderData, ".hero-slider");
   */
  fromHeroSliderBlockToCssContent(
    blockData: IHeroSliderBlockData,
    selector: string = ""
  ): ICssContent [] {
    const heroSliderCssContent: ICssContent[] = [];

    // 1. Top-level block styles
    heroSliderCssContent.push({
      selector: selector,
      styles: {
        width: blockData.width?.value
          ? `${blockData.width.value}${blockData.width.unit || "px"}`
          : "100%",
        height: blockData.height?.value
          ? `${blockData.height.value}${blockData.height.unit || "px"}`
          : "",
        "border-radius": blockData.borderRadius?.value
          ? `${blockData.borderRadius.value}${blockData.borderRadius.unit || "px"}`
          : "0px",
        "background-color": blockData.backgroundColor || "#ffffff",
      },
      css: `
      .${selector} {
        width: ${blockData.width?.value ? `${blockData.width.value}${blockData.width.unit || "px"}` : "100%"};
        height: ${blockData.height?.value ? `${blockData.height.value}${blockData.height.unit || "px"}` : "auto"};
        border-radius: ${blockData.borderRadius?.value ? `${blockData.borderRadius.value}${blockData.borderRadius.unit || "px"}` : "0"};
        background-color: ${blockData.backgroundColor || "#f8fafc"};
      }`.trim(),
    });

    // 2. Slides and sub-elements
    if (blockData.slides && blockData.slides.length > 0) {
      console.log("No. of slides:", blockData.slides.length);
      
      for (const slide of blockData.slides) {

        // --- Slide Image ---
        heroSliderCssContent.push({
          selector: slide.slideImage.selector!,
          styles: {
            "object-fit": slide.slideImage.imageFit || "cover",
            "object-position": slide.slideImage.imagePosition || "center",
          },
          css: `
          .${slide.slideImage.selector!} {
            object-fit: ${slide.slideImage.imageFit || "cover"};
            object-position: ${slide.slideImage.imagePosition || "center"};
          }`.trim()
        });

        // --- Hero Header ---
        if (slide.heroHeader) {
          heroSliderCssContent.push({
            selector: slide.heroHeader.selector!,
            styles: {
              color: slide.heroHeader.color || "#fff",
              "font-family":
                slide.heroHeader.fontFamily || "Montserrat, sans-serif",
              "font-size": slide.heroHeader.fontSize?.value
                ? `${slide.heroHeader.fontSize.value}${slide.heroHeader.fontSize.unit || "rem"}`
                : "1.875rem",
              "line-height": slide.heroHeader.lineHeight?.value
                ? `${slide.heroHeader.lineHeight.value}${slide.heroHeader.lineHeight.unit || ""}`
                : "1.2",
              "letter-spacing": slide.heroHeader.letterSpacing?.value
                ? `${slide.heroHeader.letterSpacing.value}${slide.heroHeader.letterSpacing.unit || "px"}`
                : "0px",
              margin: slide.heroHeader.margin
                ? blocksManager.dimensionToCss(slide.heroHeader.margin)
                : "4px",
              padding: slide.heroHeader.padding
                ? blocksManager.dimensionToCss(slide.heroHeader.padding)
                : "0px",
              "font-weight": slide.heroHeader.fontWeight || "700",
              "text-decoration": slide.heroHeader.textDecoration || "none",
              "text-transform": slide.heroHeader.textTransform || "none",
              "text-align": slide.heroHeader.alignment || "center",
              // Add more as needed
            },
            css: `
            .${slide.heroHeader.selector} {
              color: ${slide.heroHeader.color || "#fff"};
              font-family: ${slide.heroHeader.fontFamily || "Montserrat, sans-serif"};
              font-size: ${slide.heroHeader.fontSize?.value ? `${slide.heroHeader.fontSize.value}${slide.heroHeader.fontSize.unit || "rem"}` : "1.875rem"};
              line-height: ${slide.heroHeader.lineHeight?.value ? `${slide.heroHeader.lineHeight.value}${slide.heroHeader.lineHeight.unit || ""}` : "1.2"};
              letter-spacing: ${slide.heroHeader.letterSpacing?.value ? `${slide.heroHeader.letterSpacing.value}${slide.heroHeader.letterSpacing.unit || "px"}` : "0px"};
              margin: ${slide.heroHeader.margin ? blocksManager.dimensionToCss(slide.heroHeader.margin) : "4px"};
              padding: ${slide.heroHeader.padding ? blocksManager.dimensionToCss(slide.heroHeader.padding) : "0px"};
              font-weight: ${slide.heroHeader.fontWeight || "700"};
              text-decoration: ${slide.heroHeader.textDecoration || "none"};
              text-transform: ${slide.heroHeader.textTransform || "none"};
              text-align: ${slide.heroHeader.alignment || "center"};
              transition: all 0.15s;
            }`.trim(),
          });
        }

        // --- Hero Text ---
        if (slide.heroText) {
          heroSliderCssContent.push({
            selector: slide.heroText.selector!,
            styles: {
              color: slide.heroText.color || "#fff",
              "font-family": slide.heroText.fontFamily || "Inter, sans-serif",
              "font-size": slide.heroText.fontSize?.value
                ? `${slide.heroText.fontSize.value}${slide.heroText.fontSize.unit || "rem"}`
                : "1rem",
              "line-height": slide.heroText.lineHeight?.value
                ? `${slide.heroText.lineHeight.value}${slide.heroText.lineHeight.unit || ""}`
                : "1.5rem",
              "letter-spacing": slide.heroText.letterSpacing?.value
                ? `${slide.heroText.letterSpacing.value}${slide.heroText.letterSpacing.unit || "px"}`
                : "0px",
              margin: slide.heroText.margin
                ? blocksManager.dimensionToCss(slide.heroText.margin)
                : "4px",
              padding: slide.heroText.padding
                ? blocksManager.dimensionToCss(slide.heroText.padding)
                : "0px",
              "font-weight": slide.heroText.fontWeight || "400",
              "text-decoration": slide.heroText.textDecoration || "none",
              "text-transform": slide.heroText.textTransform || "none",
              "text-align": slide.heroText.alignment || "center",
            },
            css: `
            .${slide.heroText.selector} {
              color: ${slide.heroText.color || "#fff"};
              font-family: ${slide.heroText.fontFamily || "Inter, sans-serif"};
              font-size: ${slide.heroText.fontSize?.value ? `${slide.heroText.fontSize.value}${slide.heroText.fontSize.unit || "rem"}` : "1rem"};
              line-height: ${slide.heroText.lineHeight?.value ? `${slide.heroText.lineHeight.value}${slide.heroText.lineHeight.unit || ""}` : "1.5rem"};
              letter-spacing: ${slide.heroText.letterSpacing?.value ? `${slide.heroText.letterSpacing.value}${slide.heroText.letterSpacing.unit || "px"}` : "0px"};
              margin: ${slide.heroText.margin ? blocksManager.dimensionToCss(slide.heroText.margin) : "4px"};
              padding: ${slide.heroText.padding ? blocksManager.dimensionToCss(slide.heroText.padding) : "0px"};
              font-weight: ${slide.heroText.fontWeight || "400"};
              text-decoration: ${slide.heroText.textDecoration || "none"};
              text-transform: ${slide.heroText.textTransform || "none"};
              text-align: ${slide.heroText.alignment || "center"};
              transition: all 0.15s;
            }`.trim(),
          });
        }

        // --- CTA ---
        if (slide.cta) {
          heroSliderCssContent.push({
            selector: slide.cta.selector!,
            styles: {
              "text-align": slide.cta.alignment || "center",
              // Add other CTA-specific styles as needed
            },
            css: `
            .${slide.cta.selector} {
              text-align: ${slide.cta.alignment || "center"};
              transition: all 0.15s;
            }`.trim(),
          });
        }
      }
    }

    return heroSliderCssContent;
  }

  /**
   * Serializes a Video block's styling data into a CSS content object.
   * Generates the CSS necessary for the video element, using the provided selector as a prefix if specified.
   *
   * @param {IVideoBlockData} blockData - The data object containing configuration and styles for the Video block.
   * @param {string} [selector=""] - Optional CSS selector prefix to scope the generated styles (default is empty).
   * @returns {ICssContent} A CSS content object representing the styles for the Video block.
   *
   * @example
   * const cssObj = fromVideoBlockToCssContent(videoData, ".video-block");
   */
  fromVideoBlockToCssContent(
    blockData: IVideoBlockData,
    selector: string = ""
  ): ICssContent {
    return {
      selector: selector,
      styles: {
        padding: `${blocksManager.dimensionToCss(blockData.padding) || "0px"}`,
      },
      css: `
      .${selector} {
        padding: ${blocksManager.dimensionToCss(blockData.padding) || "0px"};
      }
      `.trim(),
    };
  }

  /**
   * Serializes a Socials block's styling data into a CSS content object.
   * Generates all necessary CSS for the social media sharing icons, using the provided selector as a prefix if specified.
   *
   * @param {ISocialsBlockData} blockData - The data object containing configuration and styles for the Socials block.
   * @param {string} [selector=""] - Optional CSS selector prefix to scope the generated styles (default is empty).
   * @returns {ICssContent} A CSS content object representing the styles for the Socials block.
   *
   * @example
   * const cssObj = fromSocialsBlockToCssContent(socialsData, ".socials-block");
   */
  fromSocialsBlockToCssContent(
    blockData: ISocialsBlockData,
    selector: string = ""
  ) {
    const {
      iconShape = "circle",
      iconSize = 32,
      alignment = "left",
      spacing = 8,
      // Add more as needed
    } = blockData;

    // Inline styles for direct React use
    const styles = {
      display: "flex",
      "align-items": "center",
      "justify-content":
        alignment === "left"
          ? "flex-start"
          : alignment === "right"
            ? "flex-end"
            : "center",
      gap: `${spacing}px`,
    };

    // CSS string for export/injection
    const css = `
    .${selector} {
      display: flex;
      align-items: center;
      justify-content: ${
        alignment === "left"
          ? "flex-start"
          : alignment === "right"
            ? "flex-end"
            : "center"
      };
      gap: ${spacing}px;
    }
    .${selector} a {
      display: flex;
      align-items: center;
      justify-content: center;
      width: ${iconSize}px;
      height: ${iconSize}px;
      background: #fff;
      border-radius: ${iconShape === "circle" ? "9999px" : "6px"};
      box-shadow: 0 1px 4px rgba(30,60,90,0.07);
      border: 1.5px solid #eeeeee;
      transition: box-shadow 0.12s, border-color 0.15s;
    }
    .${selector} a:hover {
      box-shadow: 0 4px 16px 0 rgba(30,60,90,0.18);
      border-color: #1976D2;
    }
    .${selector} svg {
      display: block;
      width: ${Math.round(iconSize * 0.65)}px;
      height: ${Math.round(iconSize * 0.65)}px;
    }`.trim();

    return {
      selector,
      styles,
      css,
    };
  }

  /**
   * Serializes a Custom HTML block's styling data into a CSS content object.
   * Generates the CSS necessary for the embedded HTML, using the provided selector as a prefix if specified.
   *
   * @param {IHtmlBlockData} blockData - The data object containing configuration and styles for the HTML block.
   * @param {string} [selector=""] - Optional CSS selector prefix to scope the generated styles (default is empty).
   * @returns {ICssContent} A CSS content object representing the styles for the HTML block.
   *
   * @example
   * const cssObj = fromHtmlBlockToCssContent(htmlBlockData, ".custom-html");
   */  
  fromHtmlBlockToCssContent(
    blockData: IHtmlBlockData,
    selector: string = ""
  ): ICssContent {
    return {
      selector: selector,
      styles: {
        padding: `${blocksManager.dimensionToCss(blockData.padding) || "0px"}`,
      },
      css: `
      .${selector} {
        padding: ${blocksManager.dimensionToCss(blockData.padding) || "0px"};
      }`.trim(),
    };
  }

  /**
   * Serializes the CSS content for all design blocks in the provided array.
   * Iterates through each block, generating the corresponding CSS content using each block’s serializer.
   * The result is a single, flat array of all CSS content objects required for rendering the full design.
   *
   * @param {IDesignBlock[]} designContent - An array of design blocks to serialize CSS for.
   * @returns {ICssContent[]} An array of CSS content objects representing the compiled styles for all blocks.
   *
   * @example
   * // Serialize all block CSS for export or preview
   * const allCssContent = serializeAllBlockCssContent(pageContent.designBlocks);
   */
  serializeAllBlockCssContent(designContent: IDesignBlock[]): ICssContent[] {
    const cssArr: ICssContent[] = [];

    for (const block of designContent) {
      switch (block.contentBlock.blockType) {
        case "columns":
          const data = block.contentBlock.blockData as IColumnsBlockData;
          const colCount = data.columnWidths?.length || data.children.length;
          const newColCssContent = this.fromColumnsBlockToCssContent(
            data,
            colCount,
            block.contentBlock.selector
          );
          contentManager.upsertCssContents(cssArr, newColCssContent);

          // Now, recursively serialize all blocks in every column
          for (const column of data.children) {
            for (const childBlock of column) {
              // Recursively collect CSS for each child block
              contentManager.upsertCssContents(cssArr, this.serializeAllBlockCssContent([childBlock]));
            }
          }

          break;
        case "button":
          const btnData = block.contentBlock.blockData as IButtonBlockData;
          // (Add suffix if you want unique class per button block)
          const newBtnCssContent = this.fromButtonBlockToCssContent(
            btnData,
            block.contentBlock.selector
          );
          contentManager.upsertCssContents(cssArr, newBtnCssContent);
          break;
        case "divider":
          const dividerData = block.contentBlock.blockData as IDividerBlockData;
          
          contentManager.upsertCssContents(cssArr, [this.fromDividerBlockToCssContent(
            dividerData,
            block.contentBlock.selector
          )]);
          break;
        case "heading":
          const headingData = block.contentBlock.blockData as IHeadingBlockData;
          
          contentManager.upsertCssContents(cssArr, [this.fromHeadingBlockToCssContent(
            headingData,
            block.contentBlock.selector
          )]);
          break;

        case "text":
          const textData = block.contentBlock.blockData as ITextBlockData;
          
          contentManager.upsertCssContents(cssArr, [this.fromTextBlockToCssContent(
            textData,
            block.contentBlock.selector
          )]);
          break;

        case "image":
          const imageData = block.contentBlock.blockData as IImageBlockData;
          
          contentManager.upsertCssContents(cssArr, [this.fromImageBlockToCssContent(
            imageData,
            block.contentBlock.selector
          )]);
          break;

        case "hero_slider":
          const heroSliderData = block.contentBlock
            .blockData as IHeroSliderBlockData;
          // (Add suffix if you want unique class per button block)
          const newCssContent = this.fromHeroSliderBlockToCssContent(
            heroSliderData,
            block.contentBlock.selector
          );
          contentManager.upsertCssContents(cssArr, newCssContent);
          break;

        case "video":
          const videoData = block.contentBlock.blockData as IVideoBlockData;
          
          contentManager.upsertCssContents(cssArr, [this.fromVideoBlockToCssContent(
            videoData,
            block.contentBlock.selector
          )]);

          break;

        case "socials":
          const socialsData = block.contentBlock.blockData as ISocialsBlockData;
          
          contentManager.upsertCssContents(cssArr, [this.fromSocialsBlockToCssContent(
            socialsData,
            block.contentBlock.selector
          )]);
          break;

        case "html":
          const htmlData = block.contentBlock.blockData as IHtmlBlockData;
          
          contentManager.upsertCssContents(cssArr, [this.fromHtmlBlockToCssContent(
            htmlData,
            block.contentBlock.selector
          )]);
          break;

        // TODO: add cases for other block types here!
        default:
          break;
      }
    }
    return cssArr;
  }
}

const serializationManager = new SerializationManager();
export default serializationManager;
